import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'

import { useGetExpertiseFormSubmissionsQuery } from '@/core/services/form'
import FormSelectionDialogContainer from '@/pages/diagnostic/containers/forms/FormSelectionDialogContainer'
import FormSubmissionsList from '@/components/forms/FormSubmissionsList'
import { useGetExpertiseQuery } from '@/core/services/expertise'

type ExpertiseFormsProps = {
    expertiseId: string
    readOnly?: boolean
    showTitle?: boolean
}

const ExpertiseForms: React.FC<ExpertiseFormsProps> = ({ expertiseId, readOnly = false, showTitle = true }) => {
    const { t } = useTranslation()

    const [isFormSelectionOpen, setIsFormSelectionOpen] = React.useState(false)
    const [page, setPage] = React.useState(0)
    const [pageSize, setPageSize] = React.useState(5)

    const { data: expertise } = useGetExpertiseQuery(expertiseId)

    const {
        data: submissionsData,
        isLoading: isSubmissionsLoading,
        error: queryError,
    } = useGetExpertiseFormSubmissionsQuery(
        {
            expertiseId: expertiseId!,
            skip: Number(page * pageSize),
            limit: Number(pageSize),
            order: 'DESC',
            orderBy: 'createdAt',
        },
        {
            refetchOnFocus: true,
            skip: !expertiseId,
            pollingInterval: 30000, // Poll every 30 seconds
        }
    )

    const handleOpenFormSelection = useCallback(() => {
        setIsFormSelectionOpen(true)
    }, [])

    const handleCloseFormSelection = useCallback(() => {
        setIsFormSelectionOpen(false)
    }, [])

    const handleFormTemplateSelect = useCallback((templateId: string) => {
        if (!expertiseId || !expertise) {
            console.error('No expertise or patient ID found')
            return
        }

        // Open form submission in new tab with all required params
        const url = `/forms/submit/${templateId}/${expertise.patient._id}?type=expertise&objectId=${expertiseId}`
        window.open(url, '_blank')
        handleCloseFormSelection()
    }, [expertiseId, expertise])

    const handleEditDraft = useCallback((submissionId: string) => {
        const submission = submissionsData?.items.find(s => s._id === submissionId)
        if (!submission) {
            console.error('Submission not found')
            return
        }
        // Open form submission in new tab with all required params and the submission ID
        const url = `/forms/submit/${submission.template._id}/${submission.patient._id}/${submissionId}?type=expertise&objectId=${expertiseId}`
        window.open(url, '_blank')
    }, [submissionsData?.items, expertiseId])

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage)
    }, [])

    const handlePageSizeChange = useCallback((newPageSize: number) => {
        setPageSize(newPageSize)
        setPage(0)
    }, [])

    // Convert RTK Query error to standard Error
    const error = useMemo(() => {
        if (!queryError) return undefined
        if ('status' in queryError && typeof queryError.data === 'object' && queryError.data) {
            const errorData = queryError.data as { message?: string }
            return new Error(errorData?.message || 'An error occurred')
        }
        return new Error((queryError as Error).message || 'An error occurred')
    }, [queryError])

    if (isSubmissionsLoading) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography>{t('common.loading')}</Typography>
            </Box>
        )
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography color="error">
                    {error?.message || t('common.error.loadingFailed')}
                </Typography>
            </Box>
        )
    }

    return (
        <Stack spacing={3}>
            {showTitle && (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        {t('page.expertise.forms.title')}
                    </Typography>
                    {!readOnly && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={handleOpenFormSelection}
                        >
                            {t('page.expertise.forms.newForm')}
                        </Button>
                    )}
                </Stack>
            )}

            <Box>
                <FormSubmissionsList
                    submissions={submissionsData?.items || []}
                    totalCount={submissionsData?.totalCount || 0}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    isLoading={isSubmissionsLoading}
                    error={error}
                    onEditDraft={!readOnly ? handleEditDraft : undefined}
                />
            </Box>

            {!readOnly && (
                <FormSelectionDialogContainer
                    open={isFormSelectionOpen}
                    onClose={handleCloseFormSelection}
                    onSelect={handleFormTemplateSelect}
                />
            )}
        </Stack>
    )
}

export default React.memo(ExpertiseForms) 